import { Injectable, signal } from '@angular/core';
import { fromEvent } from 'rxjs';

/**
 * Coordinates dropdown open state across all ACS dropdowns so only one is open at a time.
 *
 * Each dropdown supplies a unique id (e.g. `'loc-filter'`, `'group-row-1028'`).
 * Components inject the service and route their toggle/isOpen calls through it.
 *
 * Closing on outside-click is handled by per-component document listeners that call `close()`.
 * Closing on scroll is handled globally here — dropdown panels use position:fixed relative to
 * a transformed ancestor, so they can drift into unexpected positions (e.g. over sticky headers)
 * when their anchor row scrolls inside an overflow container.
 */
@Injectable({ providedIn: 'root' })
export class AcsDropdownService {
  private _openId = signal<string | null>(null);

  constructor() {
    // Capture-phase listener catches scroll on any ancestor (document, .acs-table-wrap, etc.).
    // `scroll` doesn't bubble, so capture is required for descendant scroll events to reach here.
    // Skip when the scroll originates INSIDE an open dropdown panel (e.g. scrolling its options list).
    fromEvent<Event>(document, 'scroll', { capture: true }).subscribe((event: Event) => {
      if (this._openId() === null) return;
      const target: EventTarget | null = event.target;
      if (target instanceof Element && target.closest('.acs-dropdown__panel')) return;
      this._openId.set(null);
    });
  }

  isOpen(id: string): boolean {
    return this._openId() === id;
  }

  toggle(id: string): void {
    this._openId.update(current => (current === id ? null : id));
  }

  open(id: string): void {
    this._openId.set(id);
  }

  close(): void {
    this._openId.set(null);
  }
}
